import {AtomContainer, IAtomModel, IBondModel} from "@alchemiesolns/atomcontainer";
import { getFormat } from "./utils/formatContext";
import {lonePairs2d} from "@alchemiesolns/layout-2d";
import {v3} from "@alchemiesolns/maths";
import {svgPaperAtoms} from "./svgPaperAtoms";

export interface ElectronData {
    atomId: string,
    pos: number[],
    dir: number[],
    type: string,
    offset: number
}


export interface DirectionMap {
    id: string,
    dir?: number[]
}

export const svgElectrons = {
    calcElectronPosition: (a: IAtomModel, hasVisibleLonePairs: boolean, visibleBonds: IBondModel[], container: AtomContainer, visibleAtoms: IAtomModel[], hydrogenDir?: number[]) => {
        const count = a.radicals + (hasVisibleLonePairs ? a.lonePairs : 0);
        if (count > 0) {
            // Direction from this atom toward each bonded neighbor (do not use reverseBond +
            // getBondDirection: mutating bonds breaks ordering, and lightweight render bonds
            // must not depend on reverseBond being wired correctly).
            const bondDirections = visibleBonds
                .filter((b) => b.isAttachedTo(a))
                .map((b) => {
                    const other = b.getOtherAtom(a);
                    if (!a.pos || !other.pos) {
                        return [0, 0, 0];
                    }
                    const pFrom = [a.pos.x, a.pos.y, a.pos.z ?? 0];
                    const pTo = [other.pos.x, other.pos.y, other.pos.z ?? 0];
                    return v3.directionTo(pFrom, pTo);
                })
            if (hydrogenDir) {
                bondDirections.push(hydrogenDir);
            }
            const electronVectors = lonePairs2d.arrangeLonePairs(bondDirections, count);
            const offset = svgElectrons.calcElectronOffset(a, container, visibleAtoms);

            return electronVectors.map((dir, index) => {
                const offsetPos = v3.multiplyScalar(dir, offset);
                return {
                    atomId: a.id!,
                    pos: v3.fromXYZObj(a.pos!),
                    dir: offsetPos,
                    type: index === electronVectors.length - 1 && a.radicals > 0 ? "radical" : "lonepair",
                    offset: offset
                }
            })
        }
    },
    calcElectronPositions: (visibleLonePairAtoms: IAtomModel[], visibleRadicalAtoms: IAtomModel[], visibleBonds: IBondModel[], hydrogenDirections: DirectionMap[], container: AtomContainer, allVisAtoms: IAtomModel[]) => {
        const visibleAtoms = [...new Set([...visibleLonePairAtoms, ...visibleRadicalAtoms])];
        return visibleAtoms.reduce((acc, a) => {
            const hDir = hydrogenDirections.find(dirMap => dirMap.id === a.id);
            const hasVisLps = visibleLonePairAtoms.some(v => v.id === a.id)
            const electronData = svgElectrons.calcElectronPosition(a, hasVisLps, visibleBonds, container, allVisAtoms, hDir?.dir);
            if (electronData) {
                acc.push(...electronData)
            }
            return acc;
        }, [] as ElectronData[])
    },
    calcElectronOffset: (a: IAtomModel, container: AtomContainer, visibleAtoms: IAtomModel[]) => {
        const isPaperCarbon = svgPaperAtoms.isPaperCarbon(a, container, visibleAtoms);
        if (isPaperCarbon) {
            return getFormat().lonePair.offset * .8;
        } else {
            return getFormat().lonePair.offset;
        }
    }
}